import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import { User } from 'src/interfaces/iuser';
import MarketExplorerPage from '../MarketExplorer/MarketExplorer';
import MarketCompareTool from './MarketCompareTool';
import { useSelector } from 'react-redux';
import { COLOR_PRIMARY } from 'src/const';
import { LoadingCard } from 'src/App/HOC';
import { useParams } from 'react-router-dom';
import { COLOR_PRIMARY_2_800 } from 'src/colors';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';

// ===============================
// Main component
// ===============================

const MarketComparePage = ({
  user,
  search,
  currency,
  isLoggedIn,
  dummyMarket,
  OnBoardingSearchBox,
  handleOnSampleClick,
  setSubscriptionModalOpen,
  isMobileScreen,
  headerNav,
  handleUrlReDirection,
  source,
  handleReport,
  isMarketCompareTabManuallyClicked,
  handleSidebarClick,
  handleSidebarListItemClick,
}: {
  user: User;
  search?: any;
  currency: string;
  isLoggedIn: boolean;
  dummyMarket;
  OnBoardingSearchBox;
  handleOnSampleClick;
  setSubscriptionModalOpen;
  isMobileScreen: boolean;
  headerNav?: () => JSX.Element;
  handleUrlReDirection?;
  source;
  handleReport?: (
    method: any,
    requestBody: null | any,
    id?: string,
    storeID?: boolean,
    hideLoading?: boolean
  ) => Promise<void>;
  setReportComparePageClose?: () => void;
  isMarketCompareTabManuallyClicked?: boolean;
  handleSidebarClick?: any;
  handleSidebarListItemClick?: any;
}) => {
  const params = useParams() as any;
  const openComparePageFromURL = new URLSearchParams(window.location.search).get('comparePage');

  const [mode, setMode] = useState<'explore' | 'compare'>('explore');

  const [marketComparisons, setMarketComparisons] = useState<{ label: string; details: any }[]>([]);
  const [savedMarketsNames, setSavedMarketsNames] = useState<string[]>([]);

  const savedMarketsState = useSelector((state: any) => state.dashboard.savedMarkets);
  const { data: selectedList, loading: selectedListLoading } = savedMarketsState;

  const getSavedMarketsNames = () => {
    if (selectedList && selectedList.marketDetails) {
      return Object.keys(selectedList.marketDetails).map((marketID: string) => {
        return selectedList.marketDetails[marketID].name;
      });
    }
    return [];
  };

  useEffect(() => {
    if (openComparePageFromURL && openComparePageFromURL === 'compare') {
      setMode('compare');
      window.history.pushState({}, '', '/markets/compare');
    }
  }, [openComparePageFromURL]);

  useEffect(() => {
    setSavedMarketsNames(getSavedMarketsNames());
  }, [selectedList]);

  useEffect(() => {
    if (source == 'report') {
      setMode('compare');
    }
  }, [source]);

  useEffect(() => {
    if (marketComparisons && marketComparisons.length > 0) {
      setMode('compare');
    }
  }, [marketComparisons]);

  useEffect(() => {
    if (mode === 'explore') {
      setMarketComparisons([]);
    }
  }, [mode]);

  const getCompareCTAButton = () => {
    const isDisabled = savedMarketsNames.length < 2;
    const ButtonComponent = (
      <Button
        variant="contained"
        size="large"
        onClick={() => {
          if (!isDisabled) {
            handleSidebarClick && handleSidebarClick('main');
            handleSidebarListItemClick && handleSidebarListItemClick(0);
          }
        }}
        sx={{ mt: 2, color: 'white', backgroundColor: COLOR_PRIMARY_2_800, pointerEvents: 'auto !important' }}
        disabled={isDisabled}
      >
        Compare Markets
      </Button>
    );

    if (isDisabled) {
      return (
        <TooltipNew
          title=""
          content="Save 2 or more markets from the list above to unlock your comparison view."
        >
          {ButtonComponent}
        </TooltipNew>
      );
    }
    return ButtonComponent;
  };

  return (
    <Grid container>
      {mode === 'explore' ? (
        <MarketExplorerPage
          currency={currency}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          OnBoardingSearchBox={OnBoardingSearchBox}
          setMarketComparisons={setMarketComparisons}
          isLoggedIn={isLoggedIn}
          handleSidebarClick={handleSidebarClick}
          handleSidebarListItemClick={handleSidebarListItemClick}
        />
      ) : (
        <MarketCompareTool
          isMobileScreen={isMobileScreen}
          source={source}
          headerNav={headerNav}
          OnBoardingSearchBox={OnBoardingSearchBox}
          handleOnSampleClick={handleOnSampleClick}
          currency={currency}
          isLoggedIn={isLoggedIn}
          isMarketCompareTabManuallyClicked={isMarketCompareTabManuallyClicked}
          marketComparisons={marketComparisons}
          setMarketComparisons={setMarketComparisons}
        />
      )}

      <Grid item xs={12} display="flex" justifyContent="center" alignItems="flex-start">
        {selectedListLoading ? (
          <LoadingCard height="140px" type="bars" />
        ) : mode === 'explore' ? (
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              textAlign: 'center',
              borderRadius: 2,
              mb: '100px',
            }}
          >
            <Typography variant="h6" gutterBottom>
              {savedMarketsNames.length < 2 ? (
                'Save 2 or more markets from the list above to unlock your comparison view.'
              ) : (
                <>
                  Awesome — you’ve saved{' '}
                  <span style={{ color: COLOR_PRIMARY_2_800 }}>
                    {savedMarketsNames
                      .slice(0, 2)
                      .map(name => name.split(',')[0])
                      .join(', ')}
                  </span>{' '}
                  {savedMarketsNames.length > 2 && (
                    <>
                      and{' '}
                      <span style={{ color: COLOR_PRIMARY_2_800 }}>
                        {savedMarketsNames.length - 2} more markets
                      </span>
                    </>
                  )}
                  . Time to compare!
                </>
              )}
            </Typography>
            {getCompareCTAButton()}
          </Paper>
        ) : (
          <Box
            sx={{
              p: 4,
              maxWidth: 600,
              textAlign: 'center',
              mb: '100px',
            }}
          >
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setMode('explore')}
            >
              Back to Market Explorer
            </Button>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default MarketComparePage;
